/**
 * SessionStorage-backed store for the Shop Travel (chatless) search-result
 * expiry deadline, captured from the `/hotel/search` + `/flight/search`
 * response's **`responseExpiredAt`** field (absolute UNIX-SECONDS deadline set
 * by the booking engine — e.g. `1783010920`).
 *
 * Why not reuse `linkCheckpointStore`?
 * That store also captures `responseExpiredAt`, BUT with a `Math.floor(Date.now()/1000)`
 * fallback when the field is absent — indistinguishable from a real (already-past)
 * deadline, so using it as an expiry gate would falsely read as immediately-expired.
 * Here we write ONLY when `responseExpiredAt` is actually present (finite), and a
 * missing value reads as never-expired (see resolveChatResultsExpired.ts) — no regression.
 *
 * The chat (v2) flow uses a different clock (30-min `resultsGeneratedAt`, see
 * chatResultsGeneratedAtStore.ts); this store is the Shop Travel analog. Separate
 * flight/hotel keys so a flight+hotel cart can gate on either leg independently.
 */

const FLIGHT_KEY = "_zenvoya_shop_flight_results_expiry";
const HOTEL_KEY = "_zenvoya_shop_hotel_results_expiry";

function readNumber(key: string): number | undefined {
  try {
    const v = sessionStorage.getItem(key);
    if (!v) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  } catch {
    return undefined;
  }
}

function writeNumber(key: string, value: number): void {
  try {
    sessionStorage.setItem(key, String(value));
  } catch {
    /* private mode / quota — silent */
  }
}

function removeKey(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* sessionStorage unavailable (SSR / privacy mode) — nothing to clear */
  }
}

/** value = the engine's `responseExpiredAt` (UNIX SECONDS). */
export const setShopTravelFlightResultsExpiry = (value: number): void => {
  writeNumber(FLIGHT_KEY, value);
};

export const getShopTravelFlightResultsExpiry = (): number | undefined =>
  readNumber(FLIGHT_KEY);

export const setShopTravelHotelResultsExpiry = (value: number): void => {
  writeNumber(HOTEL_KEY, value);
};

export const getShopTravelHotelResultsExpiry = (): number | undefined =>
  readNumber(HOTEL_KEY);

/** Clears both flight + hotel Shop Travel expiries (fresh search, post-booking, exit). */
export const clearShopTravelResultsExpiry = (): void => {
  removeKey(FLIGHT_KEY);
  removeKey(HOTEL_KEY);
};

/** Clears only the hotel expiry — a new stays search session is starting. */
export const clearShopTravelHotelResultsExpiry = (): void => removeKey(HOTEL_KEY);

/**
 * Marks the stays results expired now. The engine can drop a search session before
 * (or without) a stored deadline; once it has said SESSION_EXPIRED, every listing
 * action must treat the results as stale — and a re-search must not reuse the session.
 */
export const markShopTravelHotelResultsExpired = (): void =>
  writeNumber(HOTEL_KEY, Math.floor(Date.now() / 1000) - 1);

/**
 * True when a Shop Travel flight search's results are stale for the LISTING-phase
 * actions (fare-modal open, View details, Baggage & Fare Rules) — i.e. the
 * Booking-Engine session behind the fares/details calls is expired.
 *
 * Checks BOTH shop clocks (either → expired):
 *   • `flightTTL` — the legacy `TTL`-minutes deadline (ms), passed in from
 *     context. Can OUTLAST the real engine session (backend `TTL` > actual
 *     session), so on its own it lets the fares API fire and only recovers on
 *     the backend's stale-session rejection.
 *   • `responseExpiredAt` — the engine's absolute deadline (UNIX seconds) from
 *     the `/flight/search` response (getShopTravelFlightResultsExpiry). This is
 *     the ACCURATE session deadline, so consulting it lets the FE block the fares
 *     call BEFORE firing it (no skeleton-modal flash + wasted API + late refresh).
 *
 * A missing value in either clock reads as never-expired for that clock, so chat
 * flights (flightTTL null + no shop expiry) always read false here — chat expiry
 * is enforced separately via `resultsGeneratedAt` / `onBeforeOpenFareModal`.
 */
export const isShopTravelFlightResultsExpired = (flightTTL: number | null): boolean => {
  if (flightTTL !== null && Date.now() >= flightTTL) return true;
  const expirySec = getShopTravelFlightResultsExpiry();
  return Number.isFinite(expirySec) && Date.now() >= (expirySec as number) * 1000;
};

/**
 * Hotel analog of the above for the listing/select-room-phase actions (View
 * details expand, Select-a-room room pick). Hotels have NO `TTL`-minutes deadline
 * (there's no `flightTTL` equivalent) — the only Shop Travel clock is the engine's
 * `responseExpiredAt` (`getShopTravelHotelResultsExpiry`, UNIX seconds). A missing
 * value reads as never-expired (chat hotels never run the Shop-Travel
 * `/hotel/search` path, so this reads false for them; chat uses `resultsGeneratedAt`).
 */
export const isShopTravelHotelResultsExpired = (): boolean => {
  const expirySec = getShopTravelHotelResultsExpiry();
  return Number.isFinite(expirySec) && Date.now() >= (expirySec as number) * 1000;
};
